import { Box, u, Heading4, FolderStarIcon, ClubIcon, colors } from '@ui';

import { ClubTableModal } from '../ClubTableModal';

import { type ActivityFilterProps } from './ActivityFilter';
import * as css from './ActivityFilter.css';
import { ActivityTagFilter } from './components/ActivityTagFilter';
import { DateFilterInput } from './components/DateInput';
import { KeywordSearch } from './components/KeywordSearch';
import { MobileActivityFilterLayout } from './components/MobileActivityFilterLayout';
import { useDebouncedDateFilter } from './useDebounceDateFilter';
import { useSharedActivityFilter } from './useSharedActivityFilters';

export const MobileActivityFilter = ({ totalActivities, numberOfActivities, golfers }: ActivityFilterProps) => {
    const {
        initialStartDate,
        initialEndDate,
        startDate,
        endDate,
        clubModalOpen,
        selectedClubGroup,
        selectedClubLabel,
        noClubSelected,
        clubCollections,
        emptyFilter,
        filter,
        updateFilter,
        clearFilter,
        setClubModalOpen,
        handleKeywordChange,
        toggleOnlyFavorites,
        openClubModal,
        handleClubSelect,
        handleNoClubSelect,
        handleClubGroupSelect,
    } = useSharedActivityFilter();

    const handleUpdateStartDate = useDebouncedDateFilter(updateFilter, 'startDate');
    const handleUpdateEndDate = useDebouncedDateFilter(updateFilter, 'endDate');

    return (
        <MobileActivityFilterLayout
            numberOfVisibleActivities={numberOfActivities}
            totalActivities={totalActivities}
            emptyFilter={emptyFilter}
            clearFilter={clearFilter}
        >
            <Box display="flex" flexDirection="column" px={u(2)} pb={u(1.5)} gap={u(2)}>
                {golfers}

                <Box display="flex" flexDirection="column" gap={u(1.5)}>
                    <Heading4>Date range</Heading4>
                    <Box display="flex" gap={u(1)}>
                        <DateFilterInput
                            label="From"
                            value={startDate}
                            min={initialStartDate}
                            max={endDate}
                            onChange={handleUpdateStartDate}
                        />

                        <DateFilterInput
                            label="To"
                            value={endDate}
                            min={startDate}
                            max={initialEndDate}
                            onChange={handleUpdateEndDate}
                        />
                    </Box>
                </Box>
                <ActivityTagFilter />
                <div
                    className={css.keywordSearchContainer({ visible: !!filter.favorites })}
                    aria-hidden={!filter.favorites}
                >
                    <div className={css.keywordSearchInner}>
                        <KeywordSearch
                            value={filter.keywords ?? ''}
                            onChange={handleKeywordChange}
                            disabled={!filter.favorites}
                            placeholder="Filter by keywords"
                        />
                    </div>
                </div>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <button
                        type="button"
                        className={css.bottomButton({ active: !!filter.favorites })}
                        onClick={toggleOnlyFavorites}
                    >
                        <FolderStarIcon width={u(2)} />
                        <Heading4>Favorites</Heading4>
                    </button>
                    <button
                        type="button"
                        className={css.bottomButton({ active: !!selectedClubLabel })}
                        onClick={openClubModal}
                    >
                        <ClubIcon width={u(2)} color={selectedClubLabel ? colors.blue[500] : colors.bluegray[600]} />{' '}
                        <Heading4>{selectedClubLabel ? selectedClubLabel : 'Club filter'}</Heading4>
                    </button>
                </Box>
                {clubModalOpen && (
                    <ClubTableModal
                        modalOpen={clubModalOpen}
                        setModalOpen={setClubModalOpen}
                        onClubClick={handleClubSelect}
                        selectedClub={filter.clubId}
                        canSelectClubGroup
                        selectedClubGroup={selectedClubGroup}
                        onClubGroupClick={handleClubGroupSelect}
                        noClubSelected={noClubSelected}
                        onNoClubClick={handleNoClubSelect}
                        clubs={clubCollections}
                    />
                )}
            </Box>
        </MobileActivityFilterLayout>
    );
};
